import { Link } from 'react-router-dom';
import type { TastingExperience } from '@wineryxplorer/shared';
import { ManagementRowActions } from '../ui/ManagementRowActions';

// One tasting experience in the venue-experiences manager. Same row grammar as
// `venueWines/VenueWineRow.tsx` and the public `components/WineRow.tsx` —
// leading gutter value, serif name linking to the detail page, dot-joined
// sub-line, price on the right, hairline rule between rows — so the management
// lists read as one product rather than as a generic card dashboard (#349).
// The parent still owns the query, the delete mutation and its confirm dialog.
interface VenueExperienceRowProps {
  experience: TastingExperience;
  isDeleting: boolean;
  onEdit: (experienceId: string) => void;
  onDelete: (experienceId: string) => void;
}

// Same duration grammar the public `Experiences/TastingCard.tsx` uses, so an
// experience reads the same length in the manager as it does on the venue page.
const formatDuration = (mins?: number): string => {
  if (!mins) return '—';
  if (mins < 60) return `${mins} min`;

  const hours = Math.floor(mins / 60);
  const remainder = mins % 60;
  return remainder === 0 ? `${hours} hr` : `${hours} hr ${remainder} min`;
};

export const VenueExperienceRow = ({
  experience,
  isDeleting,
  onEdit,
  onDelete,
}: VenueExperienceRowProps) => {
  // Duration and price are already columns, so the sub-line carries the two
  // booking conditions that change how an experience is sold.
  const subLine = [
    experience.requiresReservation ? 'Reservation required' : undefined,
    experience.wineClubOnly ? 'Wine club exclusive' : undefined,
  ]
    .filter((part): part is string => !!part)
    .join(' · ');

  return (
    <div className="flex items-center gap-4 border-b border-gray-200 px-4 py-3.5 transition-colors last:border-b-0 hover:bg-gray-100">
      <span className="min-w-[84px] flex-shrink-0 text-base text-gray-600">
        {formatDuration(experience.duration)}
      </span>

      <Link to={`/tasting-experiences/${experience.id}`} className="group min-w-0 flex-1">
        <div className="truncate font-serif text-lg leading-tight text-gray-900 group-hover:text-primary-700">
          {experience.name}
        </div>
        {subLine && <div className="mt-0.5 truncate text-sm text-gray-600">{subLine}</div>}
      </Link>

      <span className="min-w-[60px] flex-shrink-0 text-right text-base text-gray-700">
        ${Math.round(experience.price.amount)}
      </span>

      <ManagementRowActions
        itemId={experience.id}
        itemName={experience.name}
        isDeleting={isDeleting}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
};
